// ─── Global Spot Price Bus ──────────────────────────────────
// A lightweight pub/sub that decouples SSE streams (useCtraderStream,
// BackgroundStreamManager) from consumers (TrailingStopEngine).
// Multiple streams can publish to the same bus — the engine just
// processes every tick it receives.

export interface SpotTick {
  symbolId: number;
  bid: number | null;
  ask: number | null;
  timestamp: number;
}

type SpotListener = (tick: SpotTick) => void;

class CtraderPriceBusImpl {
  private listeners: Set<SpotListener> = new Set();
  private lastTickBySymbol: Record<number, SpotTick> = {};

  /** Subscribe to all spot price ticks. Returns unsubscribe function. */
  subscribe(fn: SpotListener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /** Publish a spot tick to all listeners. Call from SSE stream handlers. */
  publish(tick: SpotTick): void {
    if (tick.symbolId !== null && tick.symbolId !== undefined) {
      this.lastTickBySymbol[tick.symbolId] = tick;
    }
    this.listeners.forEach((fn) => {
      try { fn(tick); } catch { /* don't let one bad listener break others */ }
    });
  }

  /** Get the last known tick for a symbol (for late subscribers). */
  getLastTick(symbolId: number): SpotTick | undefined {
    return this.lastTickBySymbol[symbolId];
  }

  /** Number of active listeners. */
  get listenerCount(): number {
    return this.listeners.size;
  }
}

export const ctraderPriceBus = new CtraderPriceBusImpl();